export type SafeConsoleMetadata = Record<string, unknown>;

type SafeConsoleValue = string | number | boolean | null;

const SENSITIVE_METADATA_KEY = /(?:authorization|token|secret|password|credential|signature|digest|storage|bucket|key|url|uri|xml|payload|actor|user|recipient|location|reason|comment|metadata|provider|document)/i;
const SENSITIVE_VALUE = /(?:Bearer\s+\S+|X-Amz-[A-Za-z-]+|token=|secret=|signature=|authorization=|password=|signedDigest=|storageKey=|actorId=|userId=|s3:\/\/|<[^>]+>)/i;

const sanitizeStringValue = (value: string): string => {
  if (
    SENSITIVE_VALUE.test(value) ||
    /eyJ[\w-]+\.[\w-]+\.[\w-]+/.test(value) ||
    /(?:^|\b)[a-fA-F0-9]{64}(?:\b|$)/.test(value) ||
    /AKIA[0-9A-Z]{16}/i.test(value) ||
    value.length > 160
  ) {
    return '[REDACTED]';
  }
  return value;
};

export const buildSafeConsoleMetadata = (
  context: string,
  metadata: SafeConsoleMetadata = {}
): Record<string, SafeConsoleValue> => {
  const safeMetadata: Record<string, SafeConsoleValue> = {
    context,
  };

  for (const [key, value] of Object.entries(metadata)) {
    if (SENSITIVE_METADATA_KEY.test(key)) {
      continue;
    }

    if (typeof value === 'string') {
      safeMetadata[key] = sanitizeStringValue(value);
      continue;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      safeMetadata[key] = value;
      continue;
    }

    if (value === null) {
      safeMetadata[key] = null;
    }
  }

  return safeMetadata;
};

export const safeConsoleError = (
  message: string,
  context: string,
  metadata?: SafeConsoleMetadata
): void => {
  console.error(message, buildSafeConsoleMetadata(context, metadata));
};

export const safeConsoleWarn = (
  message: string,
  context: string,
  metadata?: SafeConsoleMetadata
): void => {
  console.warn(message, buildSafeConsoleMetadata(context, metadata));
};
